<script lang="ts" setup>
import { useMotionAllowed } from '~kui/composables/useMotionPresets';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Props {
  /** The app's own motion switch, on top of the OS preference. Off, the dots sit still. */
  animated?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  animated: true,
});

const colors = [
  'rgb(196 255 87)',
  'rgb(168 156 255)',
  'rgb(255 172 56)',
  'rgb(255 135 147)',
  'rgb(255 186 245)',
  'rgb(152 235 255)',
];

const CYCLE_MS = 1400;

const colorIndex = ref(0);

const moving = useMotionAllowed(() => props.animated);

let interval: ReturnType<typeof setInterval> | undefined;

const stopCycle = () => {
  clearInterval(interval);
  interval = undefined;
};

const syncCycle = () => {
  stopCycle();
  if (!moving.value) {
    return;
  }
  interval = setInterval(() => {
    colorIndex.value = (colorIndex.value + 1) % colors.length;
  }, CYCLE_MS);
};

onMounted(() => {
  syncCycle();
  watch(moving, syncCycle);
});

onBeforeUnmount(stopCycle);
</script>

<!-- Decorative: what the coach is doing is announced as text by the live regions around it. -->
<template>
  <div
    aria-hidden="true"
    class="flex items-center gap-4"
  >
    <span
      v-for="i in 3"
      :key="i"
      data-testid="coach-loader-dot"
      class="transition-[background-color] duration-300 ease-[ease] size-3 rounded-full blur-[2px]"
      :class="moving ? 'motion-safe:animate-loading-colored-dot' : ''"
      :style="{
        'background-color': colors[colorIndex],
        'animation-delay': `${(i - 1) * 180}ms`,
      }"
    />
  </div>
</template>
